/**
 * Encompass Contacts Section (Closing tab — item 14/15)
 *
 * The Closing tab's focused excerpt of the linked Encompass template: an
 * "Investor Template Contacts" section with the HMDA Purchaser Type + Loan Trade
 * Management (Bulk Sale + Delivery Time Frame) at the top, then exactly three
 * contact tiles — Ship To, Payment, Insurance — as label/value rows with a muted
 * em-dash for missing data (no red "Missing" treatment here). The full tile grid
 * remains on the Investor Templates tab.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { ChevronDown, ChevronRight, FileText, AlertCircle, AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '@/components/ui/collapsible';
import { Skeleton } from '@/components/ui/skeleton';
import { SyncStatusBadge } from './SyncStatusBadge';
import { investorEncompassTemplateService } from '@/services/investorEncompassTemplateService';
import type {
  EncompassContactType,
  EncompassTemplateContact,
  InvestorEncompassTemplate
} from '@/types/investor-encompass';
import { formatHmdaPurchaserType, getHmdaPurchaserCode } from '@/utils/investor/hmda';
import { formatPhoneNumber, telHref } from '@/utils/investor/validation';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('EncompassContactsSection', 'ui');

interface EncompassContactsSectionProps {
  investorId: string;
  defaultOpen?: boolean;
}

/** The three contact tiles the Closing tab surfaces, in order. */
const TILE_ORDER: { type: EncompassContactType; label: string }[] = [
  { type: 'ShipTo', label: 'Ship To' },
  { type: 'Payment', label: 'Payment' },
  { type: 'Insurance', label: 'Insurance' }
];

const trimmed = (v: string | null | undefined): string | null => (v && v.trim() ? v.trim() : null);

const Dash = () => <span className="text-muted-foreground">—</span>;

const TileRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex justify-between gap-3 py-0.5 text-sm">
    <span className="text-muted-foreground">{label}</span>
    <span className="min-w-0 truncate text-right text-foreground">{children}</span>
  </div>
);

/** Ship To / Payment / Insurance tile — label/value rows, em-dash for missing, filled-count pill. */
const ClosingContactTile: React.FC<{ label: string; contact?: EncompassTemplateContact }> = ({ label, contact }) => {
  const investorName = trimmed(contact?.investorName);
  const street1 = trimmed(contact?.addressStreet1);
  const address = street1 ? [contact?.addressStreet1, contact?.addressStreet2].filter(Boolean).join(', ') : null;
  const city = trimmed(contact?.city);
  const state = trimmed(contact?.state);
  const zip = trimmed(contact?.zip);
  const contactName = trimmed(contact?.contactName);
  const phone = trimmed(contact?.phone);
  const fax = trimmed(contact?.fax);
  const email = trimmed(contact?.email);

  const filled = [investorName, street1, city, state, zip, contactName, phone, fax, email].filter(Boolean).length;
  const tel = phone ? telHref(phone) : '';

  return (
    <div className="rounded-md border bg-background p-3">
      <div className="mb-2 flex items-center justify-between">
        <div className="text-sm font-medium">{label}</div>
        <span className="rounded border bg-muted/30 px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground">{filled}/9</span>
      </div>
      <div className="divide-y divide-border/50">
        <TileRow label="Investor">{investorName ?? <Dash />}</TileRow>
        <TileRow label="Address">{address ?? <Dash />}</TileRow>
        <TileRow label="City">{city ?? <Dash />}</TileRow>
        <TileRow label="State">{state ?? <Dash />}</TileRow>
        <TileRow label="Zip">{zip ?? <Dash />}</TileRow>
        <TileRow label="Contact">{contactName ?? <Dash />}</TileRow>
        <TileRow label="Phone">{phone ? (tel ? <a className="text-primary hover:underline" href={`tel:${tel}`}>{formatPhoneNumber(phone)}</a> : phone) : <Dash />}</TileRow>
        <TileRow label="Fax">{fax ?? <Dash />}</TileRow>
        <TileRow label="Email">{email ? <a className="text-primary hover:underline" href={`mailto:${email}`}>{email}</a> : <Dash />}</TileRow>
      </div>
    </div>
  );
};

/** HMDA Purchaser Type line + Loan Trade Management (Bulk Sale chip + Delivery Time Frame). */
export const TemplateTradeHeader: React.FC<{ template: InvestorEncompassTemplate; className?: string }> = ({ template, className }) => {
  const hmda = formatHmdaPurchaserType(template);
  const bulk = template.bulkSale === null || template.bulkSale === undefined ? null : template.bulkSale;
  const days = template.deliveryTimeFrame;

  return (
    <div className={`space-y-3 ${className ?? ''}`}>
      <div className="text-sm">
        <span className="font-semibold">HMDA Purchaser Type: </span>
        {hmda ? <code>{hmda}</code> : <span className="italic text-muted-foreground">Not set</span>}
      </div>
      <div>
        <div className="mb-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Loan Trade Management</div>
        <div className="flex flex-wrap gap-x-10 gap-y-2">
          <div>
            <div className="text-xs text-muted-foreground">Bulk Sale</div>
            {bulk === null ? (
              <Dash />
            ) : (
              <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${bulk ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300' : 'bg-muted text-muted-foreground'}`}>
                {bulk ? 'Yes' : 'No'}
              </span>
            )}
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Delivery Time Frame</div>
            {days !== null && days !== undefined ? (
              <div className="leading-none"><span className="text-[18px] font-bold">{days}</span> <span className="text-sm">days</span></div>
            ) : (
              <Dash />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export const EncompassContactsSection: React.FC<EncompassContactsSectionProps> = ({
  investorId,
  defaultOpen = false
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [isLoading, setIsLoading] = useState(true);
  const [syncingTemplateId, setSyncingTemplateId] = useState<string | null>(null);
  const [templates, setTemplates] = useState<InvestorEncompassTemplate[]>([]);
  const [error, setError] = useState<string | null>(null);

  const fetchTemplates = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const allTemplates = await investorEncompassTemplateService.getTemplatesByInvestorId(investorId);
      setTemplates(allTemplates);
    } catch (err: unknown) {
      log.error('Failed to fetch templates', { investorId }, err instanceof Error ? err : undefined);
      setError(err instanceof Error ? err.message : 'Failed to load templates');
    } finally {
      setIsLoading(false);
    }
  }, [investorId]);

  useEffect(() => {
    if (isOpen || defaultOpen) {
      fetchTemplates();
    }
  }, [fetchTemplates, isOpen, defaultOpen]);

  const handleSync = async (template: InvestorEncompassTemplate) => {
    try {
      setSyncingTemplateId(template.id);
      setError(null);
      await investorEncompassTemplateService.syncTemplates({
        investorId,
        encompassTemplateId: template.encompassTemplateId,
        forceRefresh: true
      });
      await fetchTemplates();
    } catch (err: unknown) {
      log.error('Failed to sync template', { investorId, templateId: template.id }, err instanceof Error ? err : undefined);
      setError(err instanceof Error ? err.message : 'Failed to sync template');
    } finally {
      setSyncingTemplateId(null);
    }
  };

  if (isLoading && isOpen) {
    return (
      <Card className="mt-6">
        <CardHeader className="px-3.5 pt-2.5 pb-3">
          <div className="flex items-center justify-between">
            <Skeleton className="h-5 w-48" />
            <Skeleton className="h-6 w-24" />
          </div>
        </CardHeader>
        <CardContent className="px-3.5 py-2.5">
          <div className="grid gap-4 md:grid-cols-2">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-40" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error && templates.length === 0 && !isLoading) {
    return (
      <Card className="mt-6">
        <CardContent className="flex flex-col items-center justify-center px-3.5 py-8 text-center">
          <AlertCircle className="mb-3 h-10 w-10 text-destructive/50" />
          <h3 className="mb-1 text-sm font-medium">Failed to Load Templates</h3>
          <p className="mb-4 max-w-sm text-sm text-muted-foreground">{error}</p>
          <Button variant="outline" size="sm" onClick={fetchTemplates} className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (templates.length === 0 && !isLoading) {
    return (
      <Card className="mt-6 border-dashed">
        <CardContent className="flex flex-col items-center justify-center px-3.5 py-8 text-center">
          <FileText className="mb-3 h-10 w-10 text-muted-foreground/50" />
          <h3 className="mb-1 text-sm font-medium">No Encompass Templates Linked</h3>
          <p className="max-w-sm text-sm text-muted-foreground">Edit the investor to link an Encompass template.</p>
        </CardContent>
      </Card>
    );
  }

  // Closing-tab layout: ONE focused section — first-template HMDA/trade header + exactly the
  // Ship To / Payment / Insurance tiles (no per-template subheaders). For multiple linked
  // templates, the first is shown (full set lives on the Investor Templates tab) and an HMDA
  // disagreement is flagged.
  const primary = templates[0];
  const hmdaCodes = templates.map((t) => getHmdaPurchaserCode(t) ?? '∅');
  const hmdaDisagrees = new Set(hmdaCodes).size > 1;
  const primaryContact = (t: EncompassContactType) => primary?.contacts?.find((c) => c.contactType === t);

  const renderClosingSection = () => {
    if (!primary) {
      return null;
    }
    return (
      <>
        <div className="mb-4 flex items-start justify-between gap-3">
          <div className="min-w-0">
            {templates.length > 1 ? (
              <p className="text-xs text-muted-foreground">
                Showing <span className="font-medium text-foreground">{primary.templateName}</span> · {templates.length} templates linked (full set on the Investor Templates tab)
              </p>
            ) : (
              primary.investorCode && <p className="text-xs text-muted-foreground">Code: {primary.investorCode}</p>
            )}
          </div>
          <SyncStatusBadge
            status={primary.syncStatus}
            lastSyncedAt={primary.lastSyncedAt}
            syncError={primary.syncError}
            onSync={() => handleSync(primary)}
            isSyncing={syncingTemplateId === primary.id}
          />
        </div>

        <TemplateTradeHeader template={primary} className="mb-2" />
        {hmdaDisagrees && (
          <div className="mb-3 flex items-center gap-1.5 text-xs text-amber-700">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
            <span>Linked templates report different HMDA purchaser types — showing {primary.templateName}.</span>
          </div>
        )}

        <div className="grid gap-3 md:grid-cols-2">
          {TILE_ORDER.map(({ type, label }) => (
            <ClosingContactTile key={type} label={label} contact={primaryContact(type)} />
          ))}
        </div>
      </>
    );
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Card className="mt-6">
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer px-3.5 pt-2.5 pb-3 transition-colors hover:bg-muted/50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {isOpen ? (
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                )}
                <FileText className="h-4 w-4 text-muted-foreground" />
                <CardTitle className="text-xs font-medium uppercase tracking-wider">
                  Investor Template Contacts
                </CardTitle>
                <span className="text-xs text-muted-foreground">
                  ({templates.length} template{templates.length !== 1 ? 's' : ''})
                </span>
              </div>
            </div>
          </CardHeader>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <CardContent className="px-3.5 pb-2.5 pt-0">
            {error && (
              <div className="mb-4 flex items-center gap-2 text-sm text-red-500">
                <AlertCircle className="h-4 w-4" />
                <span>{error}</span>
              </div>
            )}
            {renderClosingSection()}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
};
